import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Flame, Clock, AlertTriangle, ChevronRight, Check, Bell, BellOff, Calendar, Phone, Send, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useDealershipData } from '@/data/hooks/useDealershipData';
import { useAuth } from '@/contexts/AuthContext';
import { NEXT_STEP_LABELS } from '@/types';
import type { NextStep, ActivityType, Deal } from '@/types';
import { Logo } from '@/components/Logo';
import { MobileNav } from '@/components/MobileNav';
import { ModeBadge } from '@/components/ModeBadge';
import { QuickCaptureSheet } from '@/components/QuickCaptureSheet';
import { timeAgo } from '@/data/mockData';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { HouseRulesCard } from '@/components/HouseRulesCard';
import { PaceStrip } from '@/components/rep/PaceStrip';
import { NextBestActionCard } from '@/components/rep/NextBestActionCard';

const SNOOZE_KEY = 'pullover.today.snooze.v1';
const SNOOZE_HOURS = 4;

function readSnoozes(): Record<string, number> {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, number>;
    const now = Date.now();
    // Drop expired entries on read
    return Object.fromEntries(Object.entries(parsed).filter(([, t]) => t > now));
  } catch { return {}; }
}

function writeSnoozes(s: Record<string, number>) {
  try { localStorage.setItem(SNOOZE_KEY, JSON.stringify(s)); } catch { /* noop */ }
}

// Map a next_step to the activity_type we log when the rep marks it done
const NEXT_TO_ACTIVITY: Record<NextStep, ActivityType> = {
  call: 'text_sent', // closest "outreach" type; rep can refine on the deal
  follow_up: 'note_added',
  appointment: 'appointment_set',
  send_numbers: 'quote_review',
  trade_review: 'walkaround',
  close: 'quote_review',
  sold: 'note_added',
  delivery: 'note_added',
};

const NEXT_ICON: Partial<Record<NextStep, typeof Phone>> = {
  call: Phone,
  follow_up: Send,
  appointment: Calendar,
};

type Bucket = 'overdue' | 'today' | 'upcoming' | 'no_next';

export default function Today() {
  const navigate = useNavigate();
  const { mode, userId, userName, dealershipName, dealershipId } = useAuth();
  const { deals, refresh } = useDealershipData();
  const [snoozes, setSnoozes] = useState<Record<string, number>>(() => readSnoozes());
  const [completing, setCompleting] = useState<string | null>(null);

  const repId = mode === 'demo' ? 'rep-alex' : userId;
  const myDeals = useMemo(
    () => deals.filter((d) => d.rep_id === repId && d.stage !== 'sold'),
    [deals, repId],
  );
  const now = Date.now();
  const stallMs = 24 * 60 * 60 * 1000;

  const isSnoozed = (id: string) => (snoozes[id] ?? 0) > now;

  const groups = useMemo(() => {
    const overdue: Deal[] = [];
    const today: Deal[] = [];
    const upcoming: Deal[] = [];
    const noNext: Deal[] = [];
    for (const d of myDeals) {
      if (isSnoozed(d.id)) continue;
      const ageMs = now - new Date(d.last_activity_at).getTime();
      if (!d.next_step) { noNext.push(d); continue; }
      if (d.priority === 'hot' || ageMs > stallMs) { overdue.push(d); continue; }
      if (ageMs > stallMs / 2) { today.push(d); continue; }
      upcoming.push(d);
    }
    return { overdue, today, upcoming, noNext };
  }, [myDeals, snoozes, now]);

  const snoozedCount = Object.keys(snoozes).length;

  // Re-tick once a minute so age labels and snooze expiry stay fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(i);
  }, []);

  function snooze(id: string) {
    const next = { ...snoozes, [id]: Date.now() + SNOOZE_HOURS * 60 * 60 * 1000 };
    setSnoozes(next);
    writeSnoozes(next);
    toast(`Snoozed ${SNOOZE_HOURS}h`, {
      action: {
        label: 'Undo',
        onClick: () => {
          const u = { ...next };
          delete u[id];
          setSnoozes(u);
          writeSnoozes(u);
        },
      },
    });
  }

  function unsnooze(id: string) {
    const u = { ...snoozes };
    delete u[id];
    setSnoozes(u);
    writeSnoozes(u);
  }

  async function markDone(deal: Deal) {
    if (!deal.next_step) {
      navigate(`/deals/${deal.id}`);
      return;
    }
    if (mode === 'demo') {
      toast.success(`Logged: ${NEXT_STEP_LABELS[deal.next_step]} (demo)`);
      return;
    }
    if (!dealershipId || !userId) return;
    setCompleting(deal.id);
    const activityType = NEXT_TO_ACTIVITY[deal.next_step];
    const { error: actErr } = await supabase.from('activities').insert({
      dealership_id: dealershipId,
      deal_id: deal.id,
      rep_id: userId,
      activity_type: activityType,
      details: `Completed: ${NEXT_STEP_LABELS[deal.next_step]}`,
      customer_name: deal.customer_name,
    });
    if (actErr) {
      setCompleting(null);
      toast.error('Could not log', { description: actErr.message });
      return;
    }
    // Clear next_step so the rep is forced to set the next move on the deal page
    const { error: dealErr } = await supabase
      .from('deals')
      .update({ next_step: null, last_activity_at: new Date().toISOString() })
      .eq('id', deal.id);
    setCompleting(null);
    if (dealErr) {
      toast.error('Logged, but couldn\u2019t clear next step', { description: dealErr.message });
    } else {
      toast.success('Done. Set the next step on this deal.', {
        action: { label: 'Open', onClick: () => navigate(`/deals/${deal.id}`) },
      });
    }
    refresh();
  }

  const firstName = (userName || 'You').split(' ')[0];

  const buckets: { key: Bucket; title: string; icon: typeof Flame; tone: string; deals: Deal[] }[] = [
    { key: 'overdue', title: 'Overdue & hot', icon: Flame, tone: 'text-red-400', deals: groups.overdue },
    { key: 'today', title: 'Due today', icon: Clock, tone: 'text-yellow-400', deals: groups.today },
    { key: 'no_next', title: 'Missing next step', icon: AlertTriangle, tone: 'text-orange-400', deals: groups.noNext },
    { key: 'upcoming', title: 'Upcoming', icon: Calendar, tone: 'text-muted-foreground', deals: groups.upcoming },
  ];

  const totalActions =
    groups.overdue.length + groups.today.length + groups.noNext.length;

  return (
    <div className="min-h-screen bg-background pb-24 lg:pb-0">
      <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-sm border-b border-border px-4 py-3 lg:hidden">
        <div className="flex items-center justify-between">
          <Link to="/" aria-label="PullOver.App home" className="flex items-center gap-2">
            <Logo variant="square" className="h-9 w-9 object-contain" />
            <span className="text-xs text-muted-foreground">{dealershipName || 'My Dealership'}</span>
          </Link>
          <div className="flex items-center gap-2">
            <ModeBadge />
            <span className="text-sm font-medium">{firstName}</span>
          </div>
        </div>
      </header>

      <div className="p-4 space-y-4 max-w-2xl mx-auto">
        <div>
          <h1 className="text-2xl font-bold">Today</h1>
          <p className="text-sm text-muted-foreground">
            {totalActions === 0
              ? "You're caught up. Capture the next up when it walks in."
              : `${totalActions} action${totalActions === 1 ? '' : 's'} waiting. Tap done as you go.`}
          </p>
        </div>

        <QuickCaptureSheet />

        <HouseRulesCard />
        <PaceStrip />
        <NextBestActionCard />

        {snoozedCount > 0 && (
          <button
            onClick={() => { setSnoozes({}); writeSnoozes({}); }}
            className="w-full text-xs text-muted-foreground flex items-center justify-center gap-1 py-2"
          >
            <BellOff className="h-3 w-3" /> {snoozedCount} snoozed — show all
          </button>
        )}

        {myDeals.length === 0 && (
          <Card>
            <CardContent className="p-6 text-center text-sm text-muted-foreground">
              No active deals yet. Tap <strong>Quick Capture</strong> the next time a customer walks in.
            </CardContent>
          </Card>
        )}

        {buckets.map(
          (b) =>
            b.deals.length > 0 && (
              <Card key={b.key}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 uppercase tracking-wide">
                    <b.icon className={`h-4 w-4 ${b.tone}`} /> {b.title}
                    <span className="ml-auto text-muted-foreground">{b.deals.length}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {b.deals.map((d) => {
                      const StepIcon = d.next_step ? NEXT_ICON[d.next_step] ?? ChevronRight : AlertTriangle;
                      const ageMs = now - new Date(d.last_activity_at).getTime();
                      const veryStale = ageMs > stallMs * 2;
                      return (
                        <div
                          key={d.id}
                          className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                        >
                          <Link to={`/deals/${d.id}`} className="flex items-center gap-3 min-w-0 flex-1">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                              b.key === 'overdue' ? 'bg-red-500/15 text-red-400'
                              : b.key === 'today' ? 'bg-yellow-500/15 text-yellow-400'
                              : b.key === 'no_next' ? 'bg-orange-500/15 text-orange-400'
                              : 'bg-muted text-muted-foreground'
                            }`}>
                              <StepIcon className="h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm truncate">{d.customer_name}</p>
                              <p className="text-xs text-muted-foreground truncate">{d.vehicle}</p>
                              <p className="text-xs mt-0.5">
                                {d.next_step ? (
                                  <span className="text-foreground">
                                    <strong>{NEXT_STEP_LABELS[d.next_step]}</strong>
                                  </span>
                                ) : (
                                  <span className="text-orange-400">Set a next step</span>
                                )}
                                <span className={veryStale ? 'text-red-400' : 'text-muted-foreground'}>
                                  {' · '}{timeAgo(d.last_activity_at)}
                                </span>
                              </p>
                            </div>
                          </Link>
                          {d.next_step ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-9 w-9 p-0 text-green-400 hover:text-green-300 hover:bg-green-500/10 shrink-0"
                              onClick={() => markDone(d)}
                              disabled={completing === d.id}
                              aria-label="Mark done"
                            >
                              {completing === d.id
                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                : <Check className="h-4 w-4" />}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-9 w-9 p-0 shrink-0"
                              onClick={() => navigate(`/deals/${d.id}`)}
                              aria-label="Open deal"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-9 w-9 p-0 text-muted-foreground hover:text-foreground shrink-0"
                            onClick={() => snooze(d.id)}
                            aria-label="Snooze 4 hours"
                          >
                            <Bell className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ),
        )}
      </div>

      <MobileNav />
    </div>
  );
}
